import { useMemo, useState } from 'react';

import {
  Accordion,
  ChakraBox,
  ChakraFlex,
  FAIconName,
  FireflyButton,
  useToastMessage,
  Text,
  ActionBar,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import Details from '~/components/maud/patients/treatmentEpisode/Details';
import DischargePatientModal from '~/components/maud/patients/treatmentEpisode/DischargeModal';
import FooterContent from '~/components/maud/patients/treatmentEpisode/FooterContent';
import Pause from '~/components/maud/patients/treatmentEpisode/Pause';
import TreatmentEpisodeProgram from '~/components/maud/patients/treatmentEpisode/TreatmentEpisodeProgram';
import {
  TreatmentDetails,
  TreatmentEpisodeProgramDetails,
} from '~/components/maud/patients/treatmentEpisode/Types';
import { useEditPauseState } from '~/components/maud/patients/treatmentEpisode/hooks/useEditPauseState';
import { useEditRelapsePreventionState } from '~/components/maud/patients/treatmentEpisode/hooks/useEditRelapsePreventionState';
import { useEditTreatmentEpisodeState } from '~/components/maud/patients/treatmentEpisode/hooks/useEditTreatmentEpisodeState';
import { useEditTreatmentEpisodeProgramsState } from '~/components/maud/patients/treatmentEpisode/hooks/useEditTreatmentEpisodeProgramsState';

import {
  changeLastProgramEndDate,
  createUpdateRequest,
  getDischargedPatientUpdatedPrograms,
  updateAllProgramDatesByDelta,
} from '~/components/maud/patients/treatmentEpisode/util';
import VoidTreatmentEpisodeModal from '~/components/maud/patients/treatmentEpisode/VoidModal';
import { TreatmentEpisodeStatus } from '~/lib/constants/patients.constants';
import {
  useGetPatientTreatmentEpisodesQuery,
  useUpdatePatientTreatmentEpisodeMutation,
} from '~/store/services/clinicalData';
import {
  useGetTreatmentEpisodeStatusReasonsEnumQuery,
  useGetTreatmentEpisodeStatusesEnumQuery,
} from '~/store/services/enumerations';
import { useGetInquiryDetailByIdQuery } from '~/store/services/inquiry';
import NeverStartedConfirmModal from './NeverStartedConfirmModal';
import { useTreatmentPrograms } from '~/lib/hooks/useTreatmentPrograms';

const TOP_LEVEL_ID = 'treatment-episode-accordion';

export interface TreatmentEpisodeData {
  patientExternalId: string;
  treatmentEpisodeId: string;
}

export interface TreatmentEpisodeAccordionProps {
  dismissMenu: () => void;
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  treatmentEpisodeData: TreatmentEpisodeData;
}

const AddButton = ({
  label,
  id,
  isDisabled = false,
  onClick,
}: {
  id: string;
  isDisabled?: boolean;
  label: string;
  onClick: () => void;
}) => {
  return (
    <ChakraBox>
      <FireflyButton
        id={id}
        isDisabled={isDisabled}
        leftIcon={FAIconName.plusSolid}
        onClick={onClick}
        size="small"
        variant="secondary"
      >
        {label}
      </FireflyButton>
    </ChakraBox>
  );
};

const TreatmentEpisodeAccordion = ({
  dismissMenu,
  isExpanded,
  setIsExpanded,
  treatmentEpisodeData,
}: TreatmentEpisodeAccordionProps) => {
  const { patientExternalId, treatmentEpisodeId } = treatmentEpisodeData;
  const { createToast } = useToastMessage();
  const { allPrograms: treatmentProgramOptionsResponse } =
    useTreatmentPrograms();
  const { data: episodeReasons } =
    useGetTreatmentEpisodeStatusReasonsEnumQuery();
  const { data: episodeStatuses } = useGetTreatmentEpisodeStatusesEnumQuery();

  const {
    data: patientTreatmentEpisodes,
    isFetching: isTreatmentEpisodesFetching,
  } = useGetPatientTreatmentEpisodesQuery(patientExternalId, {
    skip: !patientExternalId,
  });
  const treatmentEpisode = useMemo<TreatmentEpisode>(
    () =>
      (patientTreatmentEpisodes?.data || []).find(
        (x) => x.treatmentEpisodeId === treatmentEpisodeId,
      ),
    [patientTreatmentEpisodes, treatmentEpisodeId],
  );

  const { data: inquiry, isFetching: isInquiryFetching } =
    useGetInquiryDetailByIdQuery(treatmentEpisode?.inquiryId, {
      skip: !treatmentEpisode?.inquiryId,
    });

  const [updatePatientTreatmentEpisodes, { isLoading: isLoadingMutation }] =
    useUpdatePatientTreatmentEpisodeMutation();

  const [isReadOnly, setIsReadOnly] = useState(true);
  const [isVoidModalOpen, setIsVoidModalOpen] = useState(false);
  const [isDischargeModalOpen, setIsDischargeModalOpen] = useState(false);
  const [isNeverStartedModalOpen, setIsNeverStartedModalOpen] = useState(false);
  const treatmentEpisodeState = useEditTreatmentEpisodeState(
    treatmentEpisode,
    inquiry,
  );
  const pauseState = useEditPauseState(treatmentEpisode);
  const relapsePreventionState =
    useEditRelapsePreventionState(treatmentEpisode);
  const treatmentEpisodeProgramsState =
    useEditTreatmentEpisodeProgramsState(treatmentEpisode);

  const sendRequest = async (
    details: TreatmentDetails,
    programs?: TreatmentEpisodeProgramDetails[],
  ) => {
    return updatePatientTreatmentEpisodes({
      body: createUpdateRequest({
        pauses: pauseState.values,
        relapsePrevention: relapsePreventionState.data,
        statuses: episodeStatuses,
        statusReasons: episodeReasons,
        treatment: details,
        treatmentPrograms: programs ?? treatmentEpisodeProgramsState.values,
      }),
      inquiryId: treatmentEpisode.inquiryId,
      patientId: treatmentEpisode.patientExternalId,
      treatmentEpisodeId: treatmentEpisode.treatmentEpisodeId,
    })
      .unwrap()
      .then(() => {
        createToast({
          icon: FAIconName.checkRegular,
          id: 'treatment-episode-success_toast',
          text: 'Treatment episode saved.',
          variant: 'success',
        });
        setIsReadOnly(true);
        setIsDischargeModalOpen(false);
        setIsVoidModalOpen(false);
        dismissMenu();
      })
      .catch((e) => {
        createToast({
          icon: FAIconName.exclamationTriangleRegular,
          id: 'treatment-episode-error_toast',
          text: e.data.message,
          variant: 'critical',
        });
      });
  };

  const resetState = () => {
    treatmentEpisodeState.resetState();
    treatmentEpisodeProgramsState.resetState();
    pauseState.resetState();
    relapsePreventionState.resetState();
  };

  const saveEpisode = async () => {
    if (isLoadingMutation) return;
    await sendRequest(treatmentEpisodeState.data);
  };

  const voidEpisode = async (reason: EpisodeStatusReason) => {
    await sendRequest({
      ...treatmentEpisodeState.data,
      dischargeReason: reason.label,
      statusDescription: TreatmentEpisodeStatus.VOID,
    });
  };

  const dischargePatient = async (
    reason: EpisodeStatusReason,
    dischargeDate: Date,
  ) => {
    treatmentEpisodeState.update({
      dischargeDate,
      dischargeReason: reason.label,
      statusDescription: TreatmentEpisodeStatus.DISCHARGED,
    });
    const updatedPrograms = getDischargedPatientUpdatedPrograms(
      dischargeDate,
      treatmentEpisodeProgramsState.values,
    );

    treatmentEpisodeProgramsState.resetState(updatedPrograms);
    await sendRequest(
      {
        ...treatmentEpisodeState.data,
        dischargeDate,
        dischargeReason: reason.label,
        statusDescription: TreatmentEpisodeStatus.DISCHARGED,
      },
      updatedPrograms,
    );
  };

  const isActiveStatus = useMemo(() => {
    return (
      treatmentEpisodeState.data.statusDescription ===
      TreatmentEpisodeStatus.IN_TREATMENT
    );
  }, [treatmentEpisodeState.data.statusDescription]);

  const hasTreatmentPrograms = treatmentEpisodeProgramsState.values.length > 0;
  const hasPauseStateValues = pauseState.values.length > 0;
  const isFetching = isTreatmentEpisodesFetching || isInquiryFetching;
  const isDataChanged =
    treatmentEpisodeState.isChanged ||
    pauseState.isChanged ||
    treatmentEpisodeProgramsState.isChanged;
  const isMissingFields =
    treatmentEpisodeState.isMissingFields ||
    pauseState.isMissingFields ||
    treatmentEpisodeProgramsState.isMissingFields;
  const saveButtonDisabled =
    isLoadingMutation || isActiveStatus
      ? isFetching ||
        (!isReadOnly && (!isDataChanged || isMissingFields)) ||
        !hasTreatmentPrograms ||
        (!!treatmentEpisodeProgramsState.lastProgramEndDate && isActiveStatus)
      : isFetching || (!isReadOnly && !isDataChanged) || !hasTreatmentPrograms;
  const editButtonDisabled = isFetching || isLoadingMutation;
  const rightButtonDisabled = isReadOnly
    ? editButtonDisabled
    : saveButtonDisabled;
  const shouldShowActionBar = !isReadOnly && !hasTreatmentPrograms;
  const isAddTreatmentProgramDisabled = useMemo(() => {
    return (
      hasTreatmentPrograms && !treatmentEpisodeProgramsState.lastProgramEndDate
    );
  }, [hasTreatmentPrograms, treatmentEpisodeProgramsState.lastProgramEndDate]);

  const handleNeverStarted = () => {
    const admitDateStr = DateTime.fromJSDate(
      treatmentEpisodeState.data.admitDate,
    ).toISODate();
    const updatedPrograms = treatmentEpisodeProgramsState.values.length
      ? [
          {
            ...treatmentEpisodeProgramsState.values[0],
            endDate: admitDateStr,
          },
        ]
      : [];
    treatmentEpisodeProgramsState.resetState(updatedPrograms);
    treatmentEpisodeState.update({
      dischargeDate: treatmentEpisodeState.data.admitDate,
      statusDescription: TreatmentEpisodeStatus.NEVER_STARTED,
    });
    setIsNeverStartedModalOpen(false);
  };

  const handleUpdateStatus = (update: TreatmentDetails) => {
    switch (update.statusDescription) {
      case TreatmentEpisodeStatus.NEVER_STARTED:
        if (treatmentEpisodeProgramsState.values.length > 1) {
          setIsNeverStartedModalOpen(true);
        } else {
          handleNeverStarted();
        }
        break;

      case TreatmentEpisodeStatus.IN_TREATMENT:
        changeLastProgramEndDate(treatmentEpisodeProgramsState, null);
        treatmentEpisodeState.update(update);
        break;

      case TreatmentEpisodeStatus.DISCHARGED:
        setIsDischargeModalOpen(true);
        break;

      case TreatmentEpisodeStatus.VOID:
        setIsVoidModalOpen(true);
        break;

      default:
        treatmentEpisodeState.update(update);
        break;
    }
  };

  const handleUpdateAdmitDate = (update: TreatmentDetails) => {
    treatmentEpisodeState.update({
      ...update,
    });

    if (hasTreatmentPrograms) {
      const initialAdmitDate = treatmentEpisodeState.data.admitDate;
      const admitDate = update.admitDate;

      const deltaDays = Math.round(
        DateTime.fromJSDate(admitDate).diff(
          DateTime.fromJSDate(initialAdmitDate),
          'days',
        ).days,
      );
      updateAllProgramDatesByDelta(treatmentEpisodeProgramsState, deltaDays);
    }
  };

  const handleUpdateDischargeDate = (update: TreatmentDetails) => {
    const { dischargeDate } = update;
    const normalizedDischargeDate =
      DateTime.fromJSDate(dischargeDate).toJSDate();
    const updatedPrograms = getDischargedPatientUpdatedPrograms(
      normalizedDischargeDate,
      treatmentEpisodeProgramsState.values,
    );
    treatmentEpisodeState.update({
      dischargeDate: normalizedDischargeDate,
    });
    treatmentEpisodeProgramsState.resetState(updatedPrograms, true);
  };

  const handleChangeDetails = (update: Partial<TreatmentDetails>) => {
    if (update.dischargeDate) {
      handleUpdateDischargeDate(update);
      return;
    }

    if (update.admitDate) {
      handleUpdateAdmitDate(update);
      return;
    }

    if (update.statusDescription) {
      handleUpdateStatus(update);
      return;
    }

    treatmentEpisodeState.update(update);
  };

  const footerContent = (
    <FooterContent
      isReadOnly={isReadOnly}
      leftEnabled={!isFetching && hasTreatmentPrograms}
      onCancel={() => {
        setIsReadOnly(true);
        resetState();
      }}
      onEdit={() => setIsReadOnly(false)}
      onSave={saveEpisode}
      onVoid={() => {
        setIsVoidModalOpen(true);
      }}
      rightEnabled={!rightButtonDisabled}
    />
  );

  return (
    <Accordion
      dismissMenu={dismissMenu}
      footer={footerContent}
      id={TOP_LEVEL_ID}
      isExpanded={isExpanded}
      isFullWidth
      setIsExpanded={setIsExpanded}
      title={isReadOnly ? 'Treatment episode' : 'Edit treatment episode'}
      width={isExpanded ? '1000px' : null}
    >
      <ChakraFlex
        direction="column"
        gap="36px"
        height="643px"
        paddingBottom="18px"
        paddingLeft="24px"
        paddingRight="24px"
        paddingTop="18px"
        width="100%"
      >
        <Details
          data={treatmentEpisodeState.data}
          isReadOnly={isReadOnly}
          onChange={handleChangeDetails}
        />
        {!isTreatmentEpisodesFetching && (
          <ChakraFlex direction="column" gap="12px" width="100%">
            {hasTreatmentPrograms && (
              <Text
                color="neutral.primary"
                id={`${TOP_LEVEL_ID}__programs__header`}
                variant="h5"
              >
                Programs
              </Text>
            )}
            {hasTreatmentPrograms &&
              treatmentEpisodeProgramsState.values.map((p, i) => (
                <TreatmentEpisodeProgram
                  data={p}
                  index={i}
                  isActiveStatus={isActiveStatus}
                  isLastProgram={
                    i === treatmentEpisodeProgramsState.values.length - 1
                  }
                  isReadOnly={isReadOnly}
                  key={p.treatmentEpisodeProgramId}
                  onChange={treatmentEpisodeProgramsState.updateProgram}
                  onDelete={treatmentEpisodeProgramsState.removeProgram}
                  treatmentProgramOptions={treatmentProgramOptionsResponse}
                />
              ))}
            {!isReadOnly && isActiveStatus && (
              <AddButton
                id={`${TOP_LEVEL_ID}__add-treatment-episode-program`}
                isDisabled={isAddTreatmentProgramDisabled}
                label="Add program"
                onClick={() =>
                  treatmentEpisodeProgramsState.addProgram(
                    treatmentEpisodeState.data.admitDate,
                  )
                }
              />
            )}
          </ChakraFlex>
        )}

        <ChakraFlex
          direction="column"
          gap="12px"
          paddingBottom="36px"
          width="100%"
        >
          {hasPauseStateValues && (
            <Text
              color="neutral.primary"
              id={`${TOP_LEVEL_ID}__pauses__header`}
              variant="h5"
            >
              Pauses
            </Text>
          )}
          {hasPauseStateValues &&
            pauseState.values.map((p, i) => (
              <Pause
                data={p}
                index={i}
                isActiveStatus={isActiveStatus}
                isReadOnly={isReadOnly}
                key={p.key}
                onChange={pauseState.update}
                onDelete={pauseState.remove}
              />
            ))}
          {!isReadOnly && isActiveStatus && (
            <AddButton
              id={`${TOP_LEVEL_ID}__add-pause`}
              label="Add pause"
              onClick={pauseState.add}
            />
          )}
          {shouldShowActionBar && (
            <ActionBar
              barIcon={FAIconName.exclamationTriangleRegular}
              id="treatment-episode--warning"
              text="At least one program is required to save the treatment episode. Please add a program to continue."
              variant="critical"
            />
          )}
          <NeverStartedConfirmModal
            isOpen={isNeverStartedModalOpen}
            onCancel={() => setIsNeverStartedModalOpen(false)}
            onConfirm={handleNeverStarted}
          />
        </ChakraFlex>
      </ChakraFlex>
      <VoidTreatmentEpisodeModal
        isOpen={isVoidModalOpen}
        onCancel={() => setIsVoidModalOpen(false)}
        onConfirm={voidEpisode}
        statusReasons={episodeReasons}
      />
      <DischargePatientModal
        admitDate={treatmentEpisodeState.data.admitDate}
        isOpen={isDischargeModalOpen}
        onCancel={() => setIsDischargeModalOpen(false)}
        onConfirm={dischargePatient}
        programs={treatmentEpisodeProgramsState.values}
        statusReasons={episodeReasons}
      />
    </Accordion>
  );
};

export default TreatmentEpisodeAccordion;
